import { useMemo } from 'react';
import { useStore } from '../store/useStore';
import { useShallow } from 'zustand/react/shallow';
import type { Task, Goal, Event } from '../types';

/**
 * Calcul récursif de la progression d'un objectif sur toute la hiérarchie.
 *
 * Comptage = tâches directes + sous-objectifs (récursif) + événements liés
 */
function computeGoalProgress(
  goalId: string,
  tasks: Task[],
  goals: Goal[],
  events: Event[],
  visited: Set<string> = new Set(),
): { completed: number; total: number } {
  if (visited.has(goalId)) return { completed: 0, total: 0 };
  visited.add(goalId);

  // 1. Tâches directes de cet objectif
  const directTasks = tasks.filter((t) => t.goalId === goalId);
  let total = directTasks.length;
  let completed = directTasks.filter((t) => t.status === 'done').length;

  // 2. Sous-objectifs (récursif)
  const subGoals = goals.filter((g) => g.parentGoalId === goalId);
  for (const sg of subGoals) {
    const sub = computeGoalProgress(sg.id, tasks, goals, events, visited);
    total += sub.total;
    completed += sub.completed;
  }

  // 3. Événements liés à cet objectif
  const linkedEvents = events.filter((e) => e.goalId === goalId);
  for (const evt of linkedEvents) {
    const eventTasks = tasks.filter((t) => t.eventId === evt.id);
    total += eventTasks.length;
    completed += eventTasks.filter((t) => t.status === 'done').length;
  }

  return { completed, total };
}

export function useGoalProgress(goalId: string) {
  const { tasks, goals, events } = useStore(
    useShallow((state) => ({
      tasks: state.tasks,
      goals: state.goals,
      events: state.events,
    })),
  );

  return useMemo(() => {
    if (!goalId) return { completedTasks: 0, totalTasks: 0, percentage: 0 };
    const { completed, total } = computeGoalProgress(goalId, tasks, goals, events);
    return {
      completedTasks: completed,
      totalTasks: total,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  }, [goalId, tasks, goals, events]);
}
